import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import { useSelector } from 'react-redux';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { RowGroupColumn, useRowGroupColumns } from 'customHooks/useRowGroupColumns';
import { MetricOption, MetricType } from 'components/common/types/DrilldownReports.types';
import { useReportState } from 'components/common/DrilldownReports/hooks/useReportState';
import { useReportRowGroups } from 'components/common/DrilldownReports/hooks/useReportRowGroups';
import { createValueFormatter } from 'components/common/DrilldownReports/utils/valueFormatters';
import { calculateLoadTime, useLogPerformance } from 'customHooks/useLogPerformance';
import {
  ColGroupDef,
  ColumnApi,
  ColumnGroupShowType,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ValueGetterParams,
} from 'ag-grid-community';
import { parseISO } from 'date-fns';
import {
  getDirectSalesAllocationReport,
  getProgrammaticProposalAllocationReport,
  getProgrammaticSavedAllocationReport,
  getPublicAllocationReport,
} from 'modules/api/allocationReport';
import { CancelFunctions } from 'components/common/types';
import { TableHeader } from 'components/common/TableHeader';
import { HeaderBackgroundColor, TextColor } from 'components/common/TableHeader/TableHeader.types';
import { useParams } from 'react-router-dom';
import RowGroupRenderer from 'components/common/Table/RowGroupRenderer';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import LoadingOverlayComponent from 'lib/Table/LoadingOverlayComponent';
import { getCurrencyCode, getLocaleCode } from 'store/publisher/selectors';
import { Store } from '../types/Store.types';
import {
  AllocationDay,
  AllocationReportData,
  AllocationWeek,
  Frame,
  ReportType,
  SelectedLine,
} from './AllocationReport.types';
import { HOURS, PROGRAMMATIC_ROW_GROUP_CODES, ROW_GROUP_COLUMNS } from './consts';
import { HeaderGroupComponent } from './Table/CustomComponents/HeaderGroupComponent';
import { ReportCreationDateChip } from './Table/CustomComponents/ReportCreationDateChip';
import {
  generateTimeBreakdownColumnHeaders,
  redrawGroupedRowsWhenSortChanged,
  togglePinnedColumnOnColumnGroupOpen,
} from './Table/utils';
import { useExportAllocationReport } from './useExportAllocationReport';
import { getDeliveredMetricsUnavailableMessage, getReportDataError } from './noDeliveredData';
import {
  computeSOTAverage,
  findAllocationDay,
  findAllocationWeek,
  isDeliveredMetricSelected,
  isLineIneligibleForDeliveredMetrics,
  isShareOfTimeMetric,
  mergeAllocationReports,
} from './utils';
import { metricTypeToName } from '../DrilldownReports/consts';

export type ChangeLineHandler = (fieldName: string, selectedLine: SelectedLine | SelectedLine[]) => void;
export type ChangeMetricTypeHandler = (selectedMetricType: MetricType) => void;

interface DealLineSelectOptions {
  isSelectAllDefault?: boolean;
  isSelectHidden?: boolean;
  isSelectAllLocked?: boolean;
}

const getInitialSelectedLines = (
  dealLines: SelectedLine[],
  defaultSelectedLine: SelectedLine,
  { isSelectAllDefault, isSelectHidden, isSelectAllLocked }: DealLineSelectOptions,
): SelectedLine[] => {
  if (!(isSelectAllDefault || isSelectHidden || isSelectAllLocked) || dealLines.length <= 1) {
    return [defaultSelectedLine];
  }

  const selectableLines = dealLines.filter((line) => line.lineStatus !== DirectSalesStatusOption.CANCELLED);

  return selectableLines.length > 0 ? selectableLines : [defaultSelectedLine];
};

export interface AllocationReportTableParams {
  gridOptions: GridOptions;
  onGridReady: (event: GridReadyEvent<Frame[]>) => void;
  rowData: Frame[];
}

export type UseAllocationReport = ({
  dealLines,
  defaultSelectedLine,
  isDirectSales,
  publicToken,
  publicLocaleCode,
  availableMetricOptions,
  availableGroupByOptions,
  isSelectAllDefault,
  isSelectHidden,
  isSelectAllLocked,
}: {
  dealLines: SelectedLine[];
  defaultSelectedLine: SelectedLine;
  isDirectSales: boolean;
  publicToken?: string;
  publicLocaleCode?: string;
  availableMetricOptions?: MetricOption[];
  availableGroupByOptions?: string[];
  isSelectAllDefault?: boolean;
  isSelectHidden?: boolean;
  isSelectAllLocked?: boolean;
}) => UseAllocationReportReturn;

interface UseAllocationReportReturn {
  activeReportType: ReportType;
  allocationReportData?: AllocationReportData;
  allocationReportTableParams: AllocationReportTableParams;
  handleChangeLine: ChangeLineHandler;
  handleChangeMetricType: ChangeMetricTypeHandler;
  handleExportReport: () => void;
  handleFetchAllocationReport: (reportType: ReportType) => void;
  isLoading: boolean;
  isDirectSalesCampaignType: boolean;
  selectedMetricType: MetricType;
  selectedLines: SelectedLine[];
  selectedRowGroupColumns: RowGroupColumn[];
  handleToggleRowGroupColumn: (rowGroupColumns: RowGroupColumn[]) => void;
  rowGroupColumns: RowGroupColumn[];
  reportDataError?: string;
}

/* eslint-disable sonarjs/cognitive-complexity */
export const useAllocationReport: UseAllocationReport = ({
  dealLines,
  defaultSelectedLine,
  isDirectSales,
  publicToken,
  publicLocaleCode,
  availableMetricOptions = [],
  availableGroupByOptions,
  isSelectAllDefault,
  isSelectHidden,
  isSelectAllLocked,
}) => {
  const { campaignId: originalCampaignId } = useParams();
  const isDirectSalesCampaignType = isDirectSales;
  const isProgrammaticCampaignType = !isDirectSalesCampaignType;
  const logPerformance = useLogPerformance();

  const filteredRowGroupColumns = useMemo(() => {
    const baseColumns = isDirectSalesCampaignType
      ? ROW_GROUP_COLUMNS
      : ROW_GROUP_COLUMNS.filter((col) => PROGRAMMATIC_ROW_GROUP_CODES.includes(col.code));

    if (!availableGroupByOptions) {
      return baseColumns;
    }

    return baseColumns.filter((col) => availableGroupByOptions.includes(col.code));
  }, [availableGroupByOptions, isDirectSalesCampaignType]);

  const rowGroupColumns = useRowGroupColumns(
    filteredRowGroupColumns,
    (state: Store) => state.publisher.configuration.allocationReportDefaultRowGroups,
  );

  const {
    isLoading,
    startOfLoadingTimeInMs,
    columnApi,
    marketId,
    localeCode: sharedLocaleCode,
    campaignType,
    setIsLoading,
    setStartOfLoadingTimeInMs,
    setColumnApi,
    selectedMetricType,
    handleChangeMetricType,
  } = useReportState('allocation');

  const publisherLocaleCode = useSelector(getLocaleCode);
  const currencyCode = useSelector(getCurrencyCode);
  const localeCode = publicLocaleCode || publisherLocaleCode || sharedLocaleCode;

  const dealId = useSelector(getDealId);
  const proposalCampaignId = useSelector((state: Store) => state.dealManagement.temporaryDealId);

  const { selectedRowGroupColumns, handleToggleRowGroupColumn, updateRowGroupColumnState } = useReportRowGroups(
    rowGroupColumns,
    columnApi,
  );

  const [allocationReportData, setAllocationReportData] = useState<AllocationReportData>();
  const [reportDataError, setReportDataError] = useState<string>();
  const [selectedLines, setSelectedLines] = useState<SelectedLine[]>(() =>
    getInitialSelectedLines(dealLines, defaultSelectedLine, { isSelectAllDefault, isSelectHidden, isSelectAllLocked }),
  );
  const [activeReportType, setActiveReportType] = useState(defaultSelectedLine.lineDefaultReportType);
  const [rowData, setRowData] = useState<Frame[]>([]);
  const [totalAllocations, setTotalAllocations] = useState<AllocationWeek[]>([]);
  const fetchGenerationRef = useRef(0);
  const activeCancelBagsRef = useRef<CancelFunctions[]>([]);
  const gridApiRef = useRef<GridApi | null>(null);
  const columnApiRef = useRef<ColumnApi | null>(null);
  const selectedLinesRef = useRef(selectedLines);
  selectedLinesRef.current = selectedLines;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const { exportDirectSalesAllocationReport, exportProgrammaticAllocationReport } = useExportAllocationReport({
    activeReportType,
    allocationReportData,
    selectedLine: selectedLines[0],
    selectedMetricType,
    selectedLineCount: selectedLines.length,
  });

  const cancelAllActiveRequests = (): void => {
    activeCancelBagsRef.current.forEach((cancelFns) => {
      Object.values(cancelFns).forEach((cancel) => cancel());
    });
    activeCancelBagsRef.current = [];
  };

  const fetchSingleLineReport = async (
    line: SelectedLine,
    reportType: ReportType,
    lineCancelFunctions: CancelFunctions,
  ): Promise<AllocationReportData | null> => {
    if (isLineIneligibleForDeliveredMetrics(selectedMetricType, line.lineStatus)) {
      return null;
    }

    if (publicToken) {
      return getPublicAllocationReport(publicToken, line.lineId, selectedMetricType, lineCancelFunctions);
    }

    if (isDirectSalesCampaignType) {
      const campaignId = reportType === ReportType.SAVED && originalCampaignId ? originalCampaignId : dealId;

      return getDirectSalesAllocationReport(marketId, campaignId, line.lineId, selectedMetricType, lineCancelFunctions);
    }

    if (isProgrammaticCampaignType && reportType === ReportType.NOT_SAVED) {
      if (!proposalCampaignId) return null;
      return getProgrammaticProposalAllocationReport(proposalCampaignId, selectedMetricType, lineCancelFunctions);
    }

    if (isProgrammaticCampaignType && reportType === ReportType.SAVED) {
      if (!dealId || !line.lineId) return null;
      return getProgrammaticSavedAllocationReport(dealId, line.lineId, selectedMetricType, lineCancelFunctions);
    }

    return null;
  };

  const fetchAllocationReport = async (reportType: ReportType): Promise<void> => {
    const currentGeneration = ++fetchGenerationRef.current;
    cancelAllActiveRequests();

    const linesToFetch = selectedLinesRef.current;

    try {
      setIsLoading(true);
      setReportDataError(undefined);
      setActiveReportType(reportType);
      setTotalAllocations([]);
      setStartOfLoadingTimeInMs(performance.now());

      const batchCancelBags: CancelFunctions[] = linesToFetch.map(() => ({}));
      activeCancelBagsRef.current = batchCancelBags;

      const results = await Promise.allSettled(
        linesToFetch.map((line, index) => fetchSingleLineReport(line, reportType, batchCancelBags[index])),
      );

      if (fetchGenerationRef.current !== currentGeneration) return;

      const failedResults = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
      const nextReportDataError = getReportDataError(results);

      if (failedResults.length > 0 && !nextReportDataError) {
        datadogLogs.logger.warn('Allocation Report partial failure', {
          tradingType: campaignType,
          failedCount: failedResults.length,
          totalCount: linesToFetch.length,
          errors: failedResults.map((result) => result.reason),
        });
      }

      const successfulReports = results.flatMap((result) =>
        result.status === 'fulfilled' && result.value !== null ? [result.value] : [],
      );

      if (successfulReports.length === 0) {
        setAllocationReportData(undefined);
        setRowData([]);
        setReportDataError(
          nextReportDataError ?? getDeliveredMetricsUnavailableMessage(selectedMetricType, linesToFetch),
        );
        return;
      }

      setReportDataError(undefined);

      const allocationReportResponse =
        successfulReports.length === 1 ? successfulReports[0] : mergeAllocationReports(successfulReports);

      if (!allocationReportResponse) return;

      setAllocationReportData(allocationReportResponse);
      setRowData(allocationReportResponse.frames);

      if (allocationReportResponse?.totalAllocations?.length > 0) {
        setTotalAllocations(allocationReportResponse.totalAllocations);
      }
    } catch (error) {
      if (fetchGenerationRef.current !== currentGeneration) return;
      // eslint-disable-next-line no-console
      console.error('Allocation Report fetch failed:', error);
      datadogLogs.logger.error('Allocation Report fetch failed', { tradingType: campaignType, error });
      setAllocationReportData(undefined);
      setRowData([]);
    } finally {
      if (fetchGenerationRef.current === currentGeneration) {
        setIsLoading(false);
      }
    }
  };

  const updateSelectedRowGroupColumns = (colApi: ColumnApi): void => {
    const activeRowGroupColumnsIds = colApi.getRowGroupColumns().map((rowGroupColumn) => rowGroupColumn.getColId());

    const updatedRowGroupColumns = rowGroupColumns.map((rowGroupColumn) => {
      const isSelected = activeRowGroupColumnsIds.includes(rowGroupColumn.colId);

      return {
        ...rowGroupColumn,
        isSelected,
        groupOrder: isSelected ? activeRowGroupColumnsIds.indexOf(rowGroupColumn.colId) : undefined,
      };
    });

    handleToggleRowGroupColumn(updatedRowGroupColumns.filter((col) => col.isSelected));
  };

  const handleFormatValue = createValueFormatter(selectedMetricType, localeCode, currencyCode);

  const timeBreakdownColumnHeaders = useMemo(() => {
    if (allocationReportData?.startDate && allocationReportData.endDate) {
      return generateTimeBreakdownColumnHeaders(
        parseISO(allocationReportData.startDate),
        parseISO(allocationReportData.endDate),
        localeCode,
      );
    }

    return null;
  }, [allocationReportData?.startDate, allocationReportData?.endDate]);

  const configureGrid = useCallback(
    (api: GridApi, colApi: ColumnApi) => {
      if (!allocationReportData || !timeBreakdownColumnHeaders) {
        datadogLogs.logger.error('Allocation Report load failed', { tradingType: campaignType });
        api.setColumnDefs([]);
        api.showNoRowsOverlay();

        return;
      }

      const aggFunc = allocationReportData.rowGroupAggFunction;

      const columnDefinitions: ColGroupDef[] = [
        {
          headerGroupComponent: () =>
            !!allocationReportData.metaData && !isDeliveredMetricSelected(selectedMetricType)
              ? ReportCreationDateChip({
                  creationDateTime: allocationReportData.metaData.report.creationDateTime,
                })
              : undefined,
          groupId: 'timestamp',
          children: [
            {
              cellClass: 'frame-cell',
              cellClassRules: {
                'show-line': (params) => !params.node.firstChild,
              },
              colId: 'frame-id',
              field: 'frameId',
              headerComponent: TableHeader,
              headerComponentParams: {
                backgroundColor: HeaderBackgroundColor.WHITE,
                label: 'Frame ID',
                sortable: true,
                textColor: TextColor.SECONDARY,
              },
              cellRenderer: RowGroupRenderer,
              width: 170,
              pinned: 'left',
              sortable: true,
              showRowGroup: true,
            },
          ],
        },
        {
          groupId: 'total-duration',
          headerGroupComponent: HeaderGroupComponent,
          headerGroupComponentParams: {
            backgroundColor: HeaderBackgroundColor.WHITE,
            label: timeBreakdownColumnHeaders.label,
            isPinnedDuplicateHidden: true,
            headerLevel: 'Total line duration',
          },
          openByDefault: true,
          children: [
            {
              aggFunc,
              cellClass: 'data-cell',
              colId: 'frame-total',
              field: 'total',
              headerComponent: TableHeader,
              headerComponentParams: {
                backgroundColor: HeaderBackgroundColor.WHITE,
                label: isShareOfTimeMetric(selectedMetricType) ? 'Average' : 'Total',
                textColor: TextColor.SECONDARY,
              },
              openByDefault: true,
              width: 170,
              valueGetter: (params: ValueGetterParams) => params.data?.total,
              valueFormatter: handleFormatValue,
            },
            ...rowGroupColumns.map(({ colId, field, name }) => ({
              colId,
              field,
              headerValueGetter: () => name,
              hide: true,
              sortable: true,
            })),
            ...timeBreakdownColumnHeaders.weeks.map((week) => {
              const getWeekAllocation = (params: ValueGetterParams): AllocationWeek | undefined =>
                findAllocationWeek(params.data?.allocations, week.weekStartDate, week.weekIndex);

              return {
                aggFunc,
                columnGroupShow: 'open' as ColumnGroupShowType,
                headerGroupComponent: HeaderGroupComponent,
                headerGroupComponentParams: {
                  label: week.label,
                  headerLevel: 'Week',
                },
                children: [
                  {
                    aggFunc,
                    cellClass: 'data-cell',
                    columnGroupShow: 'closed',
                    field: `allocations.${week.weekStartDate}.total`,
                    headerName: '',
                    valueGetter: (params: ValueGetterParams) => getWeekAllocation(params)?.total ?? 0,
                    valueFormatter: handleFormatValue,
                  },
                  {
                    aggFunc,
                    cellClass: 'data-cell',
                    columnGroupShow: 'open',
                    field: `allocations.${week.weekStartDate}.total`,
                    headerGroupComponent: TableHeader,
                    headerGroupComponentParams: {
                      backgroundColor: HeaderBackgroundColor.SECONDARY,
                      label: 'Total',
                    },
                    valueGetter: (params: ValueGetterParams) => getWeekAllocation(params)?.total ?? 0,
                    valueFormatter: handleFormatValue,
                    children: [
                      {
                        aggFunc,
                        cellClass: 'data-cell',
                        columnGroupShow: 'open',
                        headerName: '',
                        field: `allocations.${week.weekStartDate}.total`,
                        valueGetter: (params: ValueGetterParams) => getWeekAllocation(params)?.total ?? 0,
                        valueFormatter: handleFormatValue,
                      },
                    ],
                  },
                  ...week.days.map((day, dayIndex) => {
                    const getDayAllocation = (params: ValueGetterParams): AllocationDay | undefined =>
                      findAllocationDay(getWeekAllocation(params), day.date);

                    return {
                      aggFunc,
                      columnGroupShow: 'open',
                      field: `allocations.${week.weekStartDate}.days.${dayIndex}.total`,
                      headerGroupComponent: HeaderGroupComponent,
                      headerGroupComponentParams: {
                        label: day.label,
                        headerLevel: 'Day',
                      },
                      valueGetter: (params: ValueGetterParams) => getDayAllocation(params)?.total ?? 0,
                      valueFormatter: handleFormatValue,
                      children: [
                        {
                          aggFunc,
                          cellClass: 'data-cell',
                          columnGroupShow: 'closed',
                          field: `allocations.${week.weekStartDate}.days.${dayIndex}.total`,
                          headerName: '',
                          valueGetter: (params: ValueGetterParams) => getDayAllocation(params)?.total ?? 0,
                          valueFormatter: handleFormatValue,
                        },
                        {
                          aggFunc,
                          cellClass: 'data-cell',
                          columnGroupShow: 'open',
                          field: `allocations.${week.weekStartDate}.days.${dayIndex}.total`,
                          headerComponent: TableHeader,
                          headerComponentParams: {
                            backgroundColor: HeaderBackgroundColor.SECONDARY,
                            label: 'Total',
                          },
                          valueGetter: (params: ValueGetterParams) => getDayAllocation(params)?.total ?? 0,
                          valueFormatter: handleFormatValue,
                        },
                        ...HOURS.map((hour, hourIndex) => ({
                          aggFunc,
                          cellClass: 'data-cell',
                          columnGroupShow: 'open',
                          field: `allocations.${week.weekStartDate}.days.${dayIndex}.hours.${hourIndex}`,
                          headerComponent: TableHeader,
                          headerComponentParams: {
                            backgroundColor: HeaderBackgroundColor.PRIMARY,
                            label: hour.label,
                            headerLevel: 'Hour',
                          },
                          valueGetter: (params: ValueGetterParams) => getDayAllocation(params)?.hours?.[hourIndex] ?? 0,
                          valueFormatter: handleFormatValue,
                        })),
                      ],
                    };
                  }),
                ],
              };
            }),
          ],
        },
      ];

      api.setColumnDefs(columnDefinitions);
      colApi.setColumnPinned('frame-total', 'right');
      setColumnApi(colApi);
      updateRowGroupColumnState(colApi, selectedRowGroupColumns);

      const loadTime = calculateLoadTime(startOfLoadingTimeInMs);
      logPerformance('Allocation Report loaded', loadTime);
    },
    [allocationReportData, timeBreakdownColumnHeaders, logPerformance],
  );

  const onGridReady = useCallback<AllocationReportTableParams['onGridReady']>(
    (onGridReadyEvent) => {
      gridApiRef.current = onGridReadyEvent.api;
      columnApiRef.current = onGridReadyEvent.columnApi;
      configureGrid(onGridReadyEvent.api, onGridReadyEvent.columnApi);

      if (isLoadingRef.current) {
        onGridReadyEvent.api.showLoadingOverlay();
      }
    },
    [configureGrid],
  );

  useEffect(() => {
    if (gridApiRef.current && columnApiRef.current) {
      configureGrid(gridApiRef.current, columnApiRef.current);
    }
  }, [configureGrid]);

  useEffect(() => {
    if (!gridApiRef.current) return;

    if (isLoading) {
      gridApiRef.current.showLoadingOverlay();
    } else if (allocationReportData) {
      gridApiRef.current.hideOverlay();
    } else {
      gridApiRef.current.showNoRowsOverlay();
    }
  }, [isLoading, allocationReportData]);

  const pinnedBottomRowData = useMemo(() => {
    if (totalAllocations.length === 0) return undefined;

    if (isShareOfTimeMetric(selectedMetricType) && rowData.length > 0) {
      const sotAverage = computeSOTAverage(rowData);
      return [{ allocations: sotAverage.allocations, frameId: 'Average', total: sotAverage.total }];
    }

    return [{ allocations: totalAllocations, frameId: 'Total', total: allocationReportData?.total }];
  }, [totalAllocations, selectedMetricType, rowData, allocationReportData?.total]);

  useEffect(() => {
    gridApiRef.current?.setPinnedBottomRowData(pinnedBottomRowData ?? []);
  }, [pinnedBottomRowData]);

  const gridOptions: GridOptions = {
    domLayout: 'autoHeight',
    onGridReady,
    onColumnGroupOpened: (params) =>
      togglePinnedColumnOnColumnGroupOpen(params, 'total-duration', 'frame-total', 'right'),
    suppressAsyncEvents: !publicToken,
    defaultColDef: {
      width: 120,
      resizable: true,
      suppressMenu: true,
      minWidth: 100,
    },
    onColumnRowGroupChanged: (params) => updateSelectedRowGroupColumns(params.columnApi),
    rowGroupPanelShow: 'onlyWhenGrouping',
    onSortChanged: redrawGroupedRowsWhenSortChanged,
    suppressMakeColumnVisibleAfterUnGroup: true,
    groupDisplayType: 'custom',
    suppressAggFuncInHeader: true,
    suppressMovableColumns: true,
    alwaysShowHorizontalScroll: true,
    loadingOverlayComponent: LoadingOverlayComponent,
    pinnedBottomRowData,
  };

  const handleChangeLine: ChangeLineHandler = (_, lineOrLines): void => {
    const lines = Array.isArray(lineOrLines) ? lineOrLines : [lineOrLines];

    if (lines.length === 0) return;

    setSelectedLines(lines);

    if (lines.length === 1) {
      setActiveReportType(lines[0].lineDefaultReportType);
    }
  };

  const handleChangeMetricTypeWithTracking: ChangeMetricTypeHandler = (newMetricType): void => {
    datadogRum.addAction(`click on Allocation report / ${metricTypeToName[newMetricType]} metric type`);
    handleChangeMetricType(newMetricType);
  };

  useEffect(() => {
    if (availableMetricOptions.length === 0) return;

    const isSelectedMetricAvailable = availableMetricOptions.some(({ code }) => code === selectedMetricType);

    if (!isSelectedMetricAvailable) handleChangeMetricType(MetricType.FRAMES);
  }, [availableMetricOptions, selectedMetricType]);

  useEffect(() => {
    if (selectedLines.length > 0 && selectedMetricType) fetchAllocationReport(activeReportType);
  }, [selectedLines, selectedMetricType]);

  useEffect(() => {
    return () => {
      cancelAllActiveRequests();
      gridApiRef.current = null;
      columnApiRef.current = null;
    };
  }, []);

  return {
    activeReportType,
    allocationReportTableParams: {
      gridOptions,
      onGridReady,
      rowData,
    },
    allocationReportData,
    handleChangeLine,
    handleChangeMetricType: handleChangeMetricTypeWithTracking,
    handleExportReport: isDirectSalesCampaignType
      ? exportDirectSalesAllocationReport
      : exportProgrammaticAllocationReport,
    handleFetchAllocationReport: fetchAllocationReport,
    isLoading,
    isDirectSalesCampaignType,
    selectedLines,
    selectedMetricType,
    selectedRowGroupColumns,
    handleToggleRowGroupColumn,
    rowGroupColumns,
    reportDataError,
  };
};
/* eslint-enable sonarjs/cognitive-complexity */
